// doctorSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IDoctor,
  IDoctorDegreeInfo,
  IDoctorExpDetail,
  IDoctorFAQ,
  IDoctorImage,
  IDoctorInitialState,
  IDoctorRating,
  IDoctorShiftSetting,
  IDoctorTiming,
} from "./doctorModal";
import {
  activeInactiveDoctor,
  createDoctor,
  deleteDoctor,
  deleteDoctorDegreeInfo,
  deleteDoctorExpDetail,
  deleteDoctorFAQ,
  deleteDoctorImage,
  deleteDoctorRating,
  deleteDoctorShiftSettings,
  deleteDoctorTiming,
  getAllActiveDoctors,
  getAllDoctors,
  getDoctorByID,
  getDoctorDegreeByDoctorID,
  getDoctorExpByDoctorID,
  getDoctorFAQByDoctorID,
  getDoctorImagesByDoctorID,
  getDoctorRatingsByDoctorID,
  getDoctorShiftSettingsByDoctorID,
  getDoctorTimingsByDoctorID,
  insertDoctorDegreeInfo,
  insertDoctorExpDetail,
  insertDoctorFAQ,
  insertDoctorRating,
  insertDoctorShiftSettings,
  insertDoctorTiming,
  updateDoctor,
  updateDoctorDegreeInfo,
  updateDoctorExpDetail,
  updateDoctorFAQ,
  updateDoctorRating,
  updateDoctorShiftSettings,
  updateDoctorTiming,
  uploadDoctorMultipleImages,
  uploadDoctorSingleImage,
} from "./services/doctor.service";

const initialState: IDoctorInitialState = {
  loading: false,
  formLoading: false,
  MultiImageformLoading: false,
  error: "",
  DoctorID: null,
  DoctorList: [],
  DoctorExpDetails: [],
  DoctorDegreeInfos: [],
  DoctorFAQs: [],
  DoctorRatings: [],
  DoctorTimings: [],
  DoctorShiftSettings: [],
  DoctorImages: [],
  totalCount: 0,
  DoctorDetail: {
    ID: null,
    DoctorName: "",
    Speciality: "",
    Experience: 0,
    IsMedical: false,
    MedicalRegDetails: "",
    AboutDoctor: "",
    Address: "",
    Area: "",
    CityID: 0,
    StateID: 0,
    CountryID: 0,
    Fees: 0,
    Gender: "",
    MobileNo: "",
    EmailID: "",
    HospitalAffiliation: "",
    LanguagesSpoken: "",
    MainImage: "",
    IsActive: 1,
    AverageRating: 0,
    ExpDetails: [],
    DegreeInfos: [],
    FAQs: [],
    Ratings: [],
    Timings: [],
    ShiftSettings: [],
    Images: [],
  },
  FilterDoctor: {
    DoctorName: "",
    SpecialityName: "",
    Experience: null,
    Fees: null,
    Gender: "",
    CityName: "",
    Skip: 0,
    Limit: 10,
  },
};

const doctorSlice = createSlice({
  name: "doctor",
  initialState,
  reducers: {
    clearDoctorDetails: (state) => {
      state.DoctorDetail = initialState.DoctorDetail;
    },
    setDoctorID: (state, action) => {
      state.DoctorID = action.payload;
    },
    clearDoctorID: (state) => {
      state.DoctorID = initialState.DoctorID;
    },
    clearDoctorExpDetails: (state) => {
      state.DoctorExpDetails = [];
    },
    clearDoctorDegreeInfos: (state) => {
      state.DoctorDegreeInfos = [];
    },
    clearDoctorFAQs: (state) => {
      state.DoctorFAQs = [];
    },
    clearDoctorRatings: (state) => {
      state.DoctorRatings = [];
    },
    clearDoctorTimings: (state) => {
      state.DoctorTimings = [];
    },
    clearDoctorShiftSettings: (state) => {
      state.DoctorShiftSettings = [];
    },
    clearDoctorImages: (state) => {
      state.DoctorImages = [];
    },
    setDoctorList: (state, action) => {
      state.DoctorList = action.payload;
    },
    setFilterDoctor: (state, action) => {
      state.FilterDoctor = action.payload;
    },
    clearFilterDoctor: (state) => {
      state.FilterDoctor = initialState.FilterDoctor;
    },
  },
  extraReducers: (builder) => {
    // ── GET ALL ────────────────────────────────────────────────────────────
    builder.addCase(getAllDoctors.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllDoctors.fulfilled,
      (
        state,
        action: PayloadAction<{ Data: IDoctor[]; TotalCount: number }>,
      ) => {
        state.loading = false;
        state.DoctorList = action.payload.Data || [];
        state.totalCount = action.payload.TotalCount || 0;
      },
    );
    builder.addCase(getAllDoctors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DoctorList = [];
    });

    // ── CREATE ─────────────────────────────────────────────────────────────
    builder.addCase(createDoctor.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(createDoctor.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(createDoctor.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // ── UPDATE ─────────────────────────────────────────────────────────────
    builder.addCase(updateDoctor.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(updateDoctor.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateDoctor.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // ── DELETE ─────────────────────────────────────────────────────────────
    builder.addCase(deleteDoctor.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteDoctor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // ── GET BY ID ──────────────────────────────────────────────────────────
    builder.addCase(getDoctorByID.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(getDoctorByID.fulfilled, (state, action: any) => {
      state.formLoading = false;
      state.DoctorDetail = action.payload;
    });
    builder.addCase(getDoctorByID.rejected, (state, action) => {
      state.formLoading = false;
      state.DoctorDetail = initialState.DoctorDetail;
      state.error = action.error.message || "Something went wrong";
    });

    // ── GET ALL ACTIVE ─────────────────────────────────────────────────────
    builder.addCase(getAllActiveDoctors.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllActiveDoctors.fulfilled, (state, action) => {
      state.loading = false;
      state.DoctorList = action.payload;
    });
    builder.addCase(getAllActiveDoctors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.DoctorList = [];
    });

    // ── ACTIVE / INACTIVE ──────────────────────────────────────────────────
    builder.addCase(activeInactiveDoctor.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(activeInactiveDoctor.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(activeInactiveDoctor.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // ── EXP DETAILS ────────────────────────────────────────────────────────
    builder.addCase(insertDoctorExpDetail.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertDoctorExpDetail.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertDoctorExpDetail.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateDoctorExpDetail.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(updateDoctorExpDetail.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateDoctorExpDetail.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteDoctorExpDetail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteDoctorExpDetail.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctorExpDetail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getDoctorExpByDoctorID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getDoctorExpByDoctorID.fulfilled,
      (state, action: PayloadAction<IDoctorExpDetail[]>) => {
        state.loading = false;
        state.DoctorExpDetails = action.payload || [];
      },
    );
    builder.addCase(getDoctorExpByDoctorID.rejected, (state, action) => {
      state.loading = false;
      state.DoctorExpDetails = [];
      state.error = action.error.message || "Something went wrong";
    });

    // ── DEGREE INFO ────────────────────────────────────────────────────────
    builder.addCase(insertDoctorDegreeInfo.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertDoctorDegreeInfo.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertDoctorDegreeInfo.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateDoctorDegreeInfo.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(updateDoctorDegreeInfo.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateDoctorDegreeInfo.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteDoctorDegreeInfo.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteDoctorDegreeInfo.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctorDegreeInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getDoctorDegreeByDoctorID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getDoctorDegreeByDoctorID.fulfilled,
      (state, action: PayloadAction<IDoctorDegreeInfo[]>) => {
        state.loading = false;
        state.DoctorDegreeInfos = action.payload || [];
      },
    );
    builder.addCase(getDoctorDegreeByDoctorID.rejected, (state, action) => {
      state.loading = false;
      state.DoctorDegreeInfos = [];
      state.error = action.error.message || "Something went wrong";
    });

    // ── FAQ ────────────────────────────────────────────────────────────────
    builder.addCase(insertDoctorFAQ.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertDoctorFAQ.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertDoctorFAQ.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateDoctorFAQ.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(updateDoctorFAQ.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateDoctorFAQ.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteDoctorFAQ.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteDoctorFAQ.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctorFAQ.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getDoctorFAQByDoctorID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getDoctorFAQByDoctorID.fulfilled,
      (state, action: PayloadAction<IDoctorFAQ[]>) => {
        state.loading = false;
        state.DoctorFAQs = action.payload || [];
      },
    );
    builder.addCase(getDoctorFAQByDoctorID.rejected, (state, action) => {
      state.loading = false;
      state.DoctorFAQs = [];
      state.error = action.error.message || "Something went wrong";
    });

    // ── RATINGS ────────────────────────────────────────────────────────────
    builder.addCase(insertDoctorRating.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertDoctorRating.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertDoctorRating.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateDoctorRating.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(updateDoctorRating.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateDoctorRating.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteDoctorRating.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteDoctorRating.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctorRating.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getDoctorRatingsByDoctorID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getDoctorRatingsByDoctorID.fulfilled,
      (state, action: PayloadAction<IDoctorRating[]>) => {
        state.loading = false;
        state.DoctorRatings = action.payload || [];
      },
    );
    builder.addCase(getDoctorRatingsByDoctorID.rejected, (state, action) => {
      state.loading = false;
      state.DoctorRatings = [];
      state.error = action.error.message || "Something went wrong";
    });

    // ── TIMINGS ────────────────────────────────────────────────────────────
    builder.addCase(insertDoctorTiming.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertDoctorTiming.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertDoctorTiming.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateDoctorTiming.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(updateDoctorTiming.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateDoctorTiming.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteDoctorTiming.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteDoctorTiming.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctorTiming.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getDoctorTimingsByDoctorID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getDoctorTimingsByDoctorID.fulfilled,
      (state, action: PayloadAction<IDoctorTiming[]>) => {
        state.loading = false;
        state.DoctorTimings = action.payload || [];
      },
    );
    builder.addCase(getDoctorTimingsByDoctorID.rejected, (state, action) => {
      state.loading = false;
      state.DoctorTimings = [];
      state.error = action.error.message || "Something went wrong";
    });

    // ── SHIFT SETTINGS ─────────────────────────────────────────────────────
    builder.addCase(insertDoctorShiftSettings.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertDoctorShiftSettings.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertDoctorShiftSettings.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(updateDoctorShiftSettings.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(updateDoctorShiftSettings.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(updateDoctorShiftSettings.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteDoctorShiftSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteDoctorShiftSettings.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctorShiftSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getDoctorShiftSettingsByDoctorID.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(
      getDoctorShiftSettingsByDoctorID.fulfilled,
      (state, action: PayloadAction<IDoctorShiftSetting[]>) => {
        state.formLoading = false;
        state.DoctorShiftSettings = action.payload || [];
      },
    );
    builder.addCase(
      getDoctorShiftSettingsByDoctorID.rejected,
      (state, action) => {
        state.formLoading = false;
        state.DoctorShiftSettings = [];
        state.error = action.error.message || "Something went wrong";
      },
    );

    // ── IMAGES ─────────────────────────────────────────────────────────────
    builder.addCase(uploadDoctorSingleImage.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(uploadDoctorSingleImage.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(uploadDoctorSingleImage.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(uploadDoctorMultipleImages.pending, (state) => {
      state.MultiImageformLoading = true;
    });
    builder.addCase(uploadDoctorMultipleImages.fulfilled, (state) => {
      state.MultiImageformLoading = false;
    });
    builder.addCase(uploadDoctorMultipleImages.rejected, (state, action) => {
      state.MultiImageformLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteDoctorImage.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteDoctorImage.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteDoctorImage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getDoctorImagesByDoctorID.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getDoctorImagesByDoctorID.fulfilled,
      (state, action: PayloadAction<IDoctorImage[]>) => {
        state.loading = false;
        state.DoctorImages = action.payload || [];
      },
    );
    builder.addCase(getDoctorImagesByDoctorID.rejected, (state, action) => {
      state.loading = false;
      state.DoctorImages = [];
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearDoctorDetails,
  clearDoctorID,
  setDoctorID,
  clearDoctorExpDetails,
  clearDoctorDegreeInfos,
  clearDoctorFAQs,
  clearDoctorRatings,
  clearDoctorTimings,
  clearDoctorShiftSettings,
  clearDoctorImages,
  setDoctorList,
  setFilterDoctor,
  clearFilterDoctor,
} = doctorSlice.actions;

export default doctorSlice.reducer;
